/**
 * TravelMintForm — the travel-template mint form the Apple and Google studios
 * share (a sibling of AssistanceMintForm, same host contract). Recipient +
 * travel content, with the flight OPT-IN: the "Járat hozzáadása (opcionális)"
 * section is collapsed by default and a flightless submit mints with NO flight
 * payload at all (no flightIata/flightDate keys). With the section open, the
 * behaviour is the pre-existing one, unchanged: the submit stays gated on a
 * verified lookup (`flightLookup.flight`).
 *
 * Honesty rule: live flight tracking exists ONLY when a flight is provided at
 * mint — the section's own copy says so ('Élő járatkövetés — járat
 * megadásakor'), and closing the section clears any verified lookup so a
 * stale flight can never ride along on a flightless mint.
 *
 * The platform envelope (offeredPlatforms + appleStyle/googleStyle) comes from
 * the host so this form stays platform-agnostic; the host owns the `mint`
 * mutation and renders the error + result UI around it.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useT, type Translate } from '../../i18n';
import { ymdLocalPlusDays } from '../../lib/localDate';
import type { MintPassRequest } from '../../types';
import { RecipientFields } from '../passForms/RecipientFields';
import { FlightLookupFields, TravelContentFields } from '../passForms/TravelDataFields';
import {
  buildTravelServicePayload,
  optionalFlightSchemaFields,
  recipientSchemaFields,
  travelContentSchemaFields,
} from '../passForms/mintPayloads';
import { useFlightLookup } from '../passForms/useFlightLookup';

import type { MintMutation } from './AssistanceMintForm';

// Rebuilt per language (useMemo on `t`) so zod messages follow the HU/EN
// switch — react-hook-form reads the resolver from the latest render's
// options, so a submit after the switch validates with the new copy.
const buildTravelSchema = (t: Translate) =>
  z.object({
    ...recipientSchemaFields(t),
    ...optionalFlightSchemaFields(t),
    ...travelContentSchemaFields(t),
  });
type TravelValues = z.infer<ReturnType<typeof buildTravelSchema>>;

export interface TravelMintFormProps {
  defaultEmail: string;
  mint: MintMutation;
  /**
   * The platform-specific mint envelope — exactly one style key + its
   * offeredPlatforms, spread verbatim into the request (so the Apple host
   * never leaks a googleStyle key and vice versa).
   */
  platformFields: Pick<MintPassRequest, 'offeredPlatforms' | 'appleStyle' | 'googleStyle'>;
}

export function TravelMintForm({
  defaultEmail,
  mint,
  platformFields,
}: TravelMintFormProps): JSX.Element {
  const t = useT();
  const travelSchema = useMemo(() => buildTravelSchema(t), [t]);
  const flightLookup = useFlightLookup();
  const [withFlight, setWithFlight] = useState(false);
  // Per-pass visibility window (spec 2026-08-31 Part A): how long the Wallet
  // card stays VISIBLE after landing — a Wallet-face setting only, live
  // updates keep flowing regardless. Travel-with-flight only (the arrival
  // anchors the relative options), so the control lives inside the flight
  // section and the choice is dropped whenever the mint goes flightless.
  const [visibility, setVisibility] = useState<'default' | '24' | '72' | 'custom'>('default');
  const [visibleUntilLocal, setVisibleUntilLocal] = useState('');
  const tomorrow = ymdLocalPlusDays(1);
  const { register, handleSubmit, formState, getValues } = useForm<TravelValues>({
    resolver: zodResolver(travelSchema),
    defaultValues: {
      recipientName: '',
      recipientEmail: defaultEmail,
      flightIata: '',
      flightDate: tomorrow,
      delayThresholdMinutes: 90,
      assistancePhone: '',
      loungeInfoText: '',
      esimLinkUrl: '',
    },
  });

  const toggleFlight = (): void => {
    setWithFlight((open) => {
      if (open) {
        // Closing the section clears the lookup state — a previously verified
        // flight must never ride along on a flightless mint (and the stale
        // result/error cards must not resurface on a later reopen).
        flightLookup.setFlight(null);
        flightLookup.setLookupError(null);
      }
      return !open;
    });
  };

  const onSubmit = (v: TravelValues): void => {
    // Flightless mint: NO flight keys at all — the backend treats the pass as
    // static (no flight subscription, no live status).
    const flightful = withFlight && flightLookup.flight !== null;
    const flightFields = flightful ? { flightIata: v.flightIata, flightDate: v.flightDate } : {};
    // Visibility rides ONLY on a flight-ful mint (the backend 400s it
    // anywhere else) — default sends no keys at all.
    const visibilityFields = !flightful
      ? {}
      : visibility === '24'
        ? { visibleAfterArrivalHours: 24 }
        : visibility === '72'
          ? { visibleAfterArrivalHours: 72 }
          : visibility === 'custom' && visibleUntilLocal !== ''
            ? { visibleUntil: new Date(visibleUntilLocal).toISOString() }
            : {};
    mint.mutate({
      recipientName: v.recipientName,
      recipientEmail: v.recipientEmail,
      templateKind: 'travel',
      ...flightFields,
      servicePayload: { ...buildTravelServicePayload(v), ...visibilityFields },
      classType: 'generic',
      ...platformFields,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <RecipientFields register={register} errors={formState.errors} />

      <section className="rounded-md ring-1 ring-slate-200 p-3 space-y-3">
        <button
          type="button"
          aria-expanded={withFlight}
          onClick={toggleFlight}
          className="flex w-full items-center justify-between text-sm font-semibold text-slate-800"
        >
          <span>{t('form.addFlight')}</span>
          <span aria-hidden="true">{withFlight ? '▲' : '▼'}</span>
        </button>
        <p className="text-xs text-slate-500">{t('form.flightOptionalHint')}</p>
        {withFlight ? (
          <>
            <FlightLookupFields
              register={register}
              errors={formState.errors}
              getValues={getValues}
              lookup={flightLookup}
            />
            <div className="space-y-1">
              <label
                htmlFor="travel-visibility"
                className="block text-sm font-medium text-slate-700"
              >
                {t('form.visibility.label')}
              </label>
              <select
                id="travel-visibility"
                value={visibility}
                onChange={(e) =>
                  setVisibility(e.target.value as 'default' | '24' | '72' | 'custom')
                }
                className="input"
              >
                <option value="default">{t('form.visibility.default')}</option>
                <option value="24">{t('form.visibility.h24')}</option>
                <option value="72">{t('form.visibility.d3')}</option>
                <option value="custom">{t('form.visibility.custom')}</option>
              </select>
              {visibility === 'custom' ? (
                <>
                  <label htmlFor="travel-visible-until" className="sr-only">
                    {t('form.visibility.custom')}
                  </label>
                  <input
                    id="travel-visible-until"
                    type="datetime-local"
                    value={visibleUntilLocal}
                    onChange={(e) => setVisibleUntilLocal(e.target.value)}
                    className="input"
                  />
                </>
              ) : null}
              <p className="text-xs text-slate-500">{t('form.visibility.hint')}</p>
            </div>
          </>
        ) : null}
      </section>

      <TravelContentFields register={register} errors={formState.errors} />

      <button
        type="submit"
        disabled={
          (withFlight && !flightLookup.flight) ||
          (withFlight && visibility === 'custom' && visibleUntilLocal === '') ||
          mint.isPending
        }
        className="btn-primary w-full"
      >
        {mint.isPending ? t('form.minting') : t('form.mint')}
      </button>
    </form>
  );
}
